// Cart Drawer Functionality
class CartDrawer {
  constructor() {
    this.isOpen = false;
    this.overlay = null;
    this.drawer = null;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchEndX = 0;
    this.touchEndY = 0;
    this.lastRefreshTime = 0;
    this.refreshThrottle = 100; // Minimum 100ms between refreshes
    this.pendingRefresh = null; // trailing-edge timer, so no refresh is dropped
    this.init();
  }

  init() {
    // Wait for cart manager to be available
    if (typeof window.cartManager === 'undefined') {
      setTimeout(() => this.init(), 100);
      return;
    }

    // Get drawer elements
    this.overlay = document.getElementById('cart-drawer-overlay');
    this.drawer = document.getElementById('cart-drawer');

    if (!this.overlay || !this.drawer) {
      console.error('Cart drawer elements not found');
      return;
    }

    // Bind events
    this.bindEvents();
    this.bindItemEvents();
    this.bindSwipeEvents();

    // Subscribe to cart updates to refresh the drawer content
    window.cartManager.subscribe((cartData) => {
      this.throttledRefreshDrawerContent();
    });

    // Listen for external cart data updates
    document.addEventListener('cart:data-updated', () => {
      if (this.isOpen) {
        this.throttledRefreshDrawerContent();
      }
    });

    // Also listen for item added events to refresh immediately
    document.addEventListener('cart:item-added', () => {
      if (this.isOpen) {
        this.throttledRefreshDrawerContent();
      }
    });
  }

  bindEvents() {
    // Cart links are owned by the capture-phase handler in layout/theme.liquid.
    // It is bound while the HTML is still parsing, so it catches a tap that lands
    // before this file has been fetched, and it covers every cart link rather than
    // only the one carrying id="cart-icon-link". Binding here too would run
    // openDrawer() twice per click.

    // Close drawer events
    document.getElementById('cart-drawer-close')?.addEventListener('click', (e) => {
      console.log('X button clicked');
      e.preventDefault();
      e.stopPropagation();
      this.closeDrawer();
    });

    document.getElementById('cart-drawer-close-bottom')?.addEventListener('click', (e) => {
      console.log('Close bottom button clicked');
      e.preventDefault();
      e.stopPropagation();
      this.closeDrawer();
    });

    // Close on overlay click
    this.overlay?.addEventListener('click', (e) => {
      // Only close if clicking directly on overlay, not on links or buttons
      if (e.target === this.overlay && !e.target.closest('a, button')) {
        this.closeDrawer();
      }
    });

    // Continue shopping button (using event delegation for dynamic content)
    document.addEventListener('click', (e) => {
      if (e.target.id === 'continue-shopping') {
        this.closeDrawer();
      }
    });

    // Handle "See more in cart" link - don't close drawer, let it navigate
    document.addEventListener('click', (e) => {
      if (e.target.closest('a[href*="/cart"]')) {
        // Don't prevent default - let the link work normally
        // Don't close drawer - user wants to see full cart
        console.log('See more in cart clicked - navigating to cart page');
      }
    });

    // Proceed to checkout
    document.getElementById('proceed-to-checkout')?.addEventListener('click', () => {
      window.location.href = '/checkout';
    });

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeDrawer();
      }
    });
  }

  bindSwipeEvents() {
    // Initialize drag tracking variables
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchEndX = 0;
    this.touchEndY = 0;
    this.isDragging = false;
    this.dragStartTime = 0;
    this.lastTouchX = 0;
    this.lastTouchTime = 0;
    this.velocity = 0;
    // Set while a gesture starts on a control, so swipe-to-close leaves it alone.
    this.suppressDrag = false;

    // Drawer touch handling for drag-to-close
    this.drawer.addEventListener('touchstart', (e) => {
      if (!this.isOpen) return;

      // Don't treat a tap on a control as the start of a swipe. Without this,
      // a thumb landing on the tiny +/- or remove button and drifting a few px
      // starts a drag, which preventDefault()s the touchmove and cancels the
      // click the browser would have synthesised — so the button silently does
      // nothing. Desktop never hits this because a mouse fires no touchmove,
      // which is why it only ever reproduced for customers on phones.
      this.suppressDrag = !!e.target.closest(
        'button, a, input, select, textarea, label, [role="button"], [contenteditable="true"]'
      );
      if (this.suppressDrag) return;

      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
      this.lastTouchX = this.touchStartX;
      this.dragStartTime = Date.now();
      this.lastTouchTime = this.dragStartTime;
      this.isDragging = false;
      this.velocity = 0;
    }, { passive: true });

    this.drawer.addEventListener('touchmove', (e) => {
      if (!this.isOpen || this.suppressDrag) return;

      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const deltaX = currentX - this.touchStartX;
      const deltaY = currentY - this.touchStartY;
      
      // Only start dragging if it's a clearly horizontal movement. 10px was
      // inside normal thumb slop, so ordinary taps registered as drags; 24px
      // still feels immediate for a real swipe.
      if (!this.isDragging && Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 24) {
        this.isDragging = true;
        // Add dragging class and disable transitions
        this.drawer.classList.add('dragging');
        this.drawer.style.transition = 'none';
      }
      
      if (this.isDragging) {
        e.preventDefault();
        
        // Calculate velocity for momentum
        const currentTime = Date.now();
        if (currentTime - this.lastTouchTime > 0) {
          this.velocity = (currentX - this.lastTouchX) / (currentTime - this.lastTouchTime);
        }
        this.lastTouchX = currentX;
        this.lastTouchTime = currentTime;
        
        // Only allow dragging to the right (positive deltaX)
        const translateX = Math.max(0, deltaX);
        this.drawer.style.setProperty('--drag-transform', `translateX(${translateX}px)`);
        
        // Update overlay opacity based on drag distance
        const maxDrag = 200; // Maximum drag distance
        const opacity = Math.max(0.1, 0.5 - (translateX / maxDrag) * 0.4);
        this.overlay.style.opacity = opacity;
      }
    }, { passive: false });

    this.drawer.addEventListener('touchend', (e) => {
      const wasSuppressed = this.suppressDrag;
      this.suppressDrag = false; // clear for the next gesture
      if (!this.isOpen || wasSuppressed || !this.isDragging) return;

      this.touchEndX = e.changedTouches[0].clientX;
      this.touchEndY = e.changedTouches[0].clientY;
      this.handleDragEnd();
    }, { passive: true });

    // Overlay touch handling for closing.
    // NOTE: #cart-drawer is a CHILD of #cart-drawer-overlay, so every touch on
    // the drawer also bubbles to these handlers. They need the same
    // control-tap suppression as the drawer's, or a tap on the +/- and remove
    // buttons still gets converted into a drag here.
    this.overlay.addEventListener('touchstart', (e) => {
      if (!this.isOpen) return;

      this.suppressDrag = !!e.target.closest(
        'button, a, input, select, textarea, label, [role="button"], [contenteditable="true"]'
      );
      if (this.suppressDrag) return;

      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
      this.lastTouchX = this.touchStartX;
      this.dragStartTime = Date.now();
      this.lastTouchTime = this.dragStartTime;
      this.isDragging = false;
      this.velocity = 0;
    }, { passive: true });

    this.overlay.addEventListener('touchmove', (e) => {
      if (!this.isOpen || this.suppressDrag) return;

      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const deltaX = currentX - this.touchStartX;
      const deltaY = currentY - this.touchStartY;

      // Only start dragging if it's a clearly horizontal movement (see the
      // drawer handler above for why 10px was too twitchy).
      if (!this.isDragging && Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 24) {
        this.isDragging = true;
        // Add dragging class and disable transitions
        this.drawer.classList.add('dragging');
        this.drawer.style.transition = 'none';
      }
      
      if (this.isDragging) {
        e.preventDefault();
        
        // Calculate velocity for momentum
        const currentTime = Date.now();
        if (currentTime - this.lastTouchTime > 0) {
          this.velocity = (currentX - this.lastTouchX) / (currentTime - this.lastTouchTime);
        }
        this.lastTouchX = currentX;
        this.lastTouchTime = currentTime;
        
        // Only allow dragging to the right (positive deltaX)
        const translateX = Math.max(0, deltaX);
        this.drawer.style.setProperty('--drag-transform', `translateX(${translateX}px)`);
        
        // Update overlay opacity based on drag distance
        const maxDrag = 200; // Maximum drag distance
        const opacity = Math.max(0.1, 0.5 - (translateX / maxDrag) * 0.4);
        this.overlay.style.opacity = opacity;
      }
    }, { passive: false });

    this.overlay.addEventListener('touchend', (e) => {
      const wasSuppressed = this.suppressDrag;
      this.suppressDrag = false; // clear for the next gesture
      if (!this.isOpen || wasSuppressed || !this.isDragging) return;

      this.touchEndX = e.changedTouches[0].clientX;
      this.touchEndY = e.changedTouches[0].clientY;
      this.handleDragEnd();
    }, { passive: true });
  }

  handleDragEnd() {
    if (!this.isOpen || !this.isDragging) return;

    const deltaX = this.touchEndX - this.touchStartX;
    const dragDuration = Date.now() - this.dragStartTime;
    
    // Re-enable transitions and remove dragging class
    this.drawer.classList.remove('dragging');
    this.drawer.style.transition = '';
    this.overlay.style.transition = '';
    
    // Determine if we should close or snap back
    const shouldClose = this.shouldCloseDrawer(deltaX, dragDuration);
    
    if (shouldClose) {
      // Close the drawer
      this.closeDrawer();
    } else {
      // Snap back to original position
      this.snapBack();
    }
    
    // Reset drag state
    this.isDragging = false;
    this.velocity = 0;
  }

  shouldCloseDrawer(deltaX, dragDuration) {
    // Close if dragged far enough (more than 1/3 of drawer width)
    const drawerWidth = this.drawer.offsetWidth;
    const closeThreshold = drawerWidth * 0.33;
    
    if (deltaX > closeThreshold) {
      return true;
    }
    
    // Close if velocity is high enough (fast swipe)
    const minVelocity = 0.5; // pixels per millisecond
    if (this.velocity > minVelocity && deltaX > 50) {
      return true;
    }
    
    return false;
  }

  snapBack() {
    // Reset drawer position by removing the custom property
    this.drawer.style.removeProperty('--drag-transform');
    this.overlay.style.opacity = '0.5';
  }

  openDrawer() {
    console.log('Opening cart drawer');
    this.isOpen = true;
    
    // Add classes to body - CSS will handle the rest
    document.body.classList.add('cart-drawer-open', 'overflow-hidden');

    // Trap focus inside the drawer; Escape is already handled above.
    if (window.A11yDialog) window.A11yDialog.activate(this.drawer);

    // Refresh content immediately with current cart data
    this.refreshDrawerContent();
    
    // Also trigger cart data fetch to ensure we have latest data
    window.cartManager.fetchCartData();
  }

  closeDrawer() {
    console.log('Closing cart drawer');
    this.isOpen = false;
    
    // Reset any drag transforms and remove dragging class
    this.drawer.classList.remove('dragging');
    this.drawer.style.removeProperty('--drag-transform');
    this.overlay.style.opacity = '';
    
    // Remove classes from body - CSS will handle the animation
    document.body.classList.remove('cart-drawer-open', 'overflow-hidden');

    // Release focus trap and return focus to the trigger.
    if (window.A11yDialog) window.A11yDialog.deactivate(this.drawer);
  }

  bindItemEvents() {
    // Bind events to all existing cart items
    document.addEventListener('click', (e) => {
      // Quantity decrease
      if (e.target.classList.contains('quantity-decrease')) {
        const lineKey = e.target.dataset.lineKey;
        const quantity = parseInt(e.target.dataset.quantity);
        if (quantity > 0) {
          this.updateQuantity(lineKey, quantity);
        }
      }

      // Quantity increase — respect a Tierly per-order limit if present.
      if (e.target.classList.contains('quantity-increase')) {
        if (e.target.disabled || e.target.getAttribute('aria-disabled') === 'true') return;
        const lineKey = e.target.dataset.lineKey;
        const quantity = parseInt(e.target.dataset.quantity);
        const max = parseInt(e.target.dataset.maxQuantity);
        if (max && quantity > max) return; // already at the limit
        this.updateQuantity(lineKey, quantity);
      }

      // Remove item
      if (e.target.closest('.remove-item')) {
        const lineKey = e.target.closest('.remove-item').dataset.lineKey;
        this.removeItem(lineKey);
      }
    });

    // Quantity input change
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('quantity-input')) {
        const lineKey = e.target.dataset.lineKey;
        let newQty = parseInt(e.target.value);
        // Clamp typed quantity to a Tierly per-order limit if one is set.
        const max = parseInt(e.target.getAttribute('max'));
        if (max && newQty > max) {
          newQty = max;
          e.target.value = max;
        }
        if (newQty > 0) {
          this.updateQuantity(lineKey, newQty);
        } else {
          e.target.value = 1;
        }
      }
    });
  }

  async updateQuantity(lineKey, quantity) {
    try {
      const result = await window.cartManager.updateCartItemByKey(lineKey, quantity);
      if (!result.success) {
        window.cartUI.showErrorNotification('Failed to update quantity');
      }
      // Cart manager will trigger pubsub update automatically which will refresh the drawer
    } catch (error) {
      console.error('Error updating quantity:', error);
      window.cartUI.showErrorNotification('Failed to update quantity');
    }
  }

  async removeItem(lineKey) {
    try {
      // Build the list of line keys to remove. Always includes the requested
      // line, plus any add-on lines that reference it as their parent (via the
      // _addon_of line item property, which stores the parent variant id).
      // /cart/update.js doesn't accept line keys, so the cascade runs as
      // sequential /cart/change.js calls — non-atomic, but per-line accurate
      // even when the cart has multiple lines for the same variant.
      const keysToRemove = [lineKey];

      const cartData = window.cartManager.cartData || (await window.cartManager.fetchCartData());
      if (cartData && cartData.items) {
        const parentLine = cartData.items.find((it) => it.key === lineKey);
        const parentVariantIdStr = parentLine ? String(parentLine.variant_id) : null;
        if (parentVariantIdStr) {
          for (const item of cartData.items) {
            if (item.key === lineKey) continue;
            if (this.getAddonParentId(item) === parentVariantIdStr) {
              keysToRemove.push(item.key);
            }
          }
        }
      }

      let allOk = true;
      for (const key of keysToRemove) {
        const result = await window.cartManager.removeFromCartByKey(key);
        if (!result.success) {
          allOk = false;
        }
      }

      if (allOk) {
        const removedCount = keysToRemove.length;
        const msg = removedCount > 1
          ? `Item and ${removedCount - 1} add-on${removedCount > 2 ? 's' : ''} removed from cart`
          : 'Item removed from cart';
        window.cartUI.showSuccessNotification(msg);
      } else {
        window.cartUI.showErrorNotification('Failed to remove item');
      }
      // Cart manager will trigger pubsub update automatically which will refresh the drawer
    } catch (error) {
      console.error('Error removing item:', error);
      window.cartUI.showErrorNotification('Failed to remove item');
    }
  }

  throttledRefreshDrawerContent() {
    const now = Date.now();
    const since = now - this.lastRefreshTime;

    if (since < this.refreshThrottle) {
      // Defer instead of dropping. The old version returned here, so a refresh
      // landing inside the window was lost for good — leaving the drawer
      // showing stale rows and stale data-quantity values on the +/- buttons
      // (they carry a precomputed target quantity), so the next tap re-sent a
      // quantity the cart already had and appeared to do nothing.
      if (!this.pendingRefresh) {
        this.pendingRefresh = setTimeout(() => {
          this.pendingRefresh = null;
          this.lastRefreshTime = Date.now();
          this.refreshDrawerContent();
        }, this.refreshThrottle - since);
      }
      return;
    }

    if (this.pendingRefresh) {
      clearTimeout(this.pendingRefresh);
      this.pendingRefresh = null;
    }
    this.lastRefreshTime = now;
    this.refreshDrawerContent();
  }

  refreshDrawerContent() {
    // Get the latest cart data and render it dynamically
    const cartData = window.cartManager.cartData;
    if (!cartData) return;

    this.renderCartItems(cartData);
    this.updateCartFooter(cartData);
    this.updateFreeShippingTracker(cartData);
  }

  // Read the _addon_of line item property (string variant id) if present.
  // Returns null if this is not an add-on.
  getAddonParentId(item) {
    if (!item || !item.properties) return null;
    if (Array.isArray(item.properties)) {
      const prop = item.properties.find((p) => p.name === '_addon_of');
      return prop ? String(prop.value) : null;
    }
    if (typeof item.properties === 'object') {
      return item.properties._addon_of ? String(item.properties._addon_of) : null;
    }
    return null;
  }

  renderCartItems(cartData) {
    const itemsContainer = document.getElementById('cart-drawer-items');
    const footer = document.getElementById('cart-drawer-footer');
    const tracker = document.querySelector('[data-threshold]');

    if (!itemsContainer) return;

    // Filter out hidden accessory products
    const hidden = window.HIDDEN_CART_HANDLES || [];
    const visibleItems = (cartData.items || []).filter(item => !hidden.includes(item.handle));

    // Show/hide empty state and footer
    if (visibleItems.length === 0) {
      // Show empty state
      itemsContainer.innerHTML = `
        <div id="cart-drawer-empty" class="h-full flex items-center justify-center text-center">
          <div class="flex flex-col items-center justify-center">
            <svg class="w-16 h-16 text-black mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path>
            </svg>
            <p>Your cart is empty</p>
            <button id="continue-shopping" class="mt-4 text-[#B72621] hover:underline">Continue Shopping</button>
          </div>
        </div>
      `;
      if (footer) footer.style.display = 'none';
      if (tracker) tracker.style.display = 'none';
      return;
    }

    // Hide empty state and show footer
    if (footer) footer.style.display = 'block';
    if (tracker) tracker.style.display = 'block';

    // Group add-ons under their parent line items.
    // mainItems: items that are NOT add-ons (or whose parent is not in the cart)
    // addonsByParent: { parentVariantId: [addonItem, ...] }
    const mainItems = [];
    const addonsByParent = {};
    const visibleVariantIds = new Set(visibleItems.map((i) => String(i.variant_id)));

    for (const item of visibleItems) {
      const parentId = this.getAddonParentId(item);
      if (parentId && visibleVariantIds.has(parentId)) {
        if (!addonsByParent[parentId]) addonsByParent[parentId] = [];
        addonsByParent[parentId].push(item);
      } else {
        // Either not an add-on, or an orphaned add-on whose parent isn't in cart
        mainItems.push(item);
      }
    }

    // Render each main item, with any nested add-ons inside its card
    const itemsHTML = mainItems
      .map((main) => {
        const children = addonsByParent[String(main.variant_id)] || [];
        return this.renderCartItem(main, children);
      })
      .join('');

    // Add promo banner if enabled
    const promoBannerHTML = this.renderPromoBanner();

    itemsContainer.innerHTML = itemsHTML + promoBannerHTML;

    // Free-gift row is resolved server-side and injected after paint, so a slow
    // or failed lookup can never delay the cart itself. Deliberately not awaited.
    this.refreshFreeGiftRow();
  }

  /**
   * Shows the complimentary diamond studs inside the qualifying ring's card.
   *
   * The gift is granted by the `free-studs-gift` Cart Transform function, which
   * expands the ring's line into [ring @ price, studs @ $0.00]. That expansion
   * does NOT appear in /cart.js — verified against the live store, it only
   * materialises at checkout — so the drawer would otherwise show the ring
   * alone and contradict the banner right up to the checkout page.
   *
   * Presentation only. The function is the sole thing that prices the gift, so
   * faking this row client-side buys a shopper nothing.
   */
  async refreshFreeGiftRow() {
    const container = document.getElementById('cart-drawer-items');
    if (!container) return;

    // Two cart events landing together (add + the drawer's own refresh) would
    // otherwise each clear, await, then insert — producing duplicate rows.
    // Only the newest call is allowed to write, and it re-clears after the
    // await because an older in-flight call may have inserted meanwhile.
    const seq = (this.freeGiftSeq = (this.freeGiftSeq || 0) + 1);
    container.querySelectorAll('[data-free-gift-row]').forEach((n) => n.remove());

    try {
      const res = await fetch('/?sections=free-gift-status', {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok || seq !== this.freeGiftSeq) return;

      const payload = await res.json();
      const markup = payload['free-gift-status'];
      if (!markup) return;

      const holder = new DOMParser().parseFromString(markup, 'text/html');
      const node = holder.getElementById('free-gift-status');
      if (!node) return;

      const status = JSON.parse(node.textContent);
      if (!status.eligible || !status.gift) return;

      // Attach to the first qualifying line — one gift per cart, matching the
      // single expand the function emits for it.
      const variantId = String(status.qualifyingVariantIds[0] ?? '');
      if (!variantId) return;

      const host = container
        .querySelector(`.cart-drawer-item[data-variant-id="${variantId}"]`)
        ?.closest('.cart-drawer-item-wrapper');
      if (!host) return;

      if (seq !== this.freeGiftSeq) return;
      container.querySelectorAll('[data-free-gift-row]').forEach((n) => n.remove());
      host.insertAdjacentHTML('beforeend', this.renderFreeGiftRow(status.gift));
    } catch (_) {
      // Cosmetic enhancement — never let it break the cart.
    }
  }

  renderFreeGiftRow(gift) {
    const image = gift.image
      ? `<img src="${gift.image}" alt="${this.escapeHtml(gift.title)}" width="44" height="44" loading="lazy" class="w-full h-full object-contain p-[3px]">`
      : '';

    return `
      <div data-free-gift-row class="cart-drawer-free-gift flex items-center gap-3 px-4 py-[10px]">
        <div class="w-11 h-11 flex-shrink-0 rounded-[6px] overflow-hidden bg-white ring-1 ring-[#EBDFBE]">${image}</div>

        <div class="flex-1 min-w-0">
          <p class="flex items-center gap-[5px] text-[9px] font-semibold uppercase tracking-[0.14em] text-[#A8842A] leading-none">
            <svg class="w-[11px] h-[11px] flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 2l1.9 5.3L19 9.2l-4.4 3.2 1.7 5.3L12 14.6 7.7 17.7l1.7-5.3L5 9.2l5.1-1.9L12 2z"/>
            </svg>
            Free gift
          </p>
          <p class="text-[12px] leading-[1.35] text-gray-900 mt-[4px] line-clamp-2">${this.escapeHtml(gift.title)}</p>
        </div>

        <div class="flex-shrink-0 text-right">
          <span class="inline-block text-[10px] font-semibold uppercase tracking-[0.08em] text-[#7A5F12] bg-[#F4E8C6] px-[9px] py-[3px] rounded-full leading-none">Free</span>
          <p class="text-[10px] text-[#A2977C] mt-[4px] whitespace-nowrap">at checkout</p>
        </div>
      </div>
    `;
  }

  renderPromoBanner() {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return '';
    
    const isEnabled = drawer.getAttribute('data-promo-banner-enabled') === 'true';
    if (!isEnabled) return '';
    
    const imageUrl400 = drawer.getAttribute('data-promo-banner-image-400');
    const imageUrl800 = drawer.getAttribute('data-promo-banner-image-800');
    const altText = drawer.getAttribute('data-promo-banner-alt') || 'Promotional banner';
    const linkUrl = drawer.getAttribute('data-promo-banner-link');
    
    if (!imageUrl400 && !imageUrl800) return '';
    
    // Use 800px as default for better quality, with srcset for responsive
    const srcset = imageUrl400 && imageUrl800 
      ? `srcset="${imageUrl400} 400w, ${imageUrl800} 800w" sizes="(max-width: 640px) 90vw, 400px"`
      : '';
    const src = imageUrl800 || imageUrl400;
    
    const imageHTML = `
      <img
        src="${src}"
        ${srcset ? srcset : ''}
        alt="${this.escapeHtml(altText)}"
        width="800"
        height="auto"
        class="w-full h-auto object-cover rounded-lg"
        loading="lazy"
      >
    `;
    
    if (linkUrl) {
      return `
        <div class="cart-drawer-promo-banner mt-4">
          <a href="${this.escapeHtml(linkUrl)}" class="block">
            ${imageHTML}
          </a>
        </div>
      `;
    }
    
    return `
      <div class="cart-drawer-promo-banner mt-4">
        ${imageHTML}
      </div>
    `;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  renderCartItem(item, children = []) {
    const imageUrl = item.image ? window.cartManager.formatImageUrl(item.image, 150, 160) : '';
    const itemTotal = item.final_line_price || (item.price * item.quantity);
    const originalTotal = item.original_line_price || itemTotal;
    const hasDiscount = originalTotal !== itemTotal;

    const addonRowsHTML = children.map((c) => this.renderAddonRow(c)).join('');

    // Tierly per-order purchase limit (from window.tierlyLimits, seeded by Liquid).
    // The cart JSON carries no metafields, so we look it up by product id here.
    const tLimit = (window.tierlyLimits && window.tierlyLimits[item.product_id]) || 0;
    const tAtLimit = tLimit > 0 && item.quantity >= tLimit;

    return `
      <div class="cart-drawer-item-wrapper bg-white rounded-[8px] !mb-2 overflow-hidden">
      <div class="cart-drawer-item flex items-stretch space-x-4 ${children.length > 0 ? 'border-b border-gray-100' : ''} pr-4" data-variant-id="${item.variant_id}">
        <!-- Product Image -->
        <div class="flex-shrink-0 w-[100px] lg:w-[140px]">
          <div class="w-full h-full min-h-[100px] lg:min-h-[140px] bg-white">
            <a href="${item.url}" class="block w-full h-full">
              ${imageUrl ? `
                <img
                  src="${imageUrl}"
                  alt="${item.product_title}"
                  width="150"
                  height="160"
                  class="w-full h-full object-cover"
                >
              ` : `
                <div class="w-full h-full bg-gray-200 flex items-center justify-center">
                  <span class="text-gray-400 text-sm">No image</span>
                </div>
              `}
            </a>
          </div>
        </div>

        <!-- Product Details -->
        <div class="flex-1 min-w-0 flex flex-col justify-between py-4">
          <div class="flex-1 flex flex-col gap-1 !mb-2">
            <h3 class="font-medium text-sm line-clamp-2">
              <a href="${item.url}" class="hover:text-[#B72621] transition-colors">
                ${item.product_title}
              </a>
            </h3>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `
              <p class="text-xs text-gray-600 mb-2">${item.variant_title}</p>
            ` : ''}
            ${item.sku ? `
              <p class="text-xs text-gray-500 mb-2">SKU: ${item.sku}</p>
            ` : ''}
            <div class="text-sm font-medium text-[#B72621]">
              ${window.cartManager.formatPrice(itemTotal)}
              ${hasDiscount ? `
                <span class="text-xs text-gray-500 line-through ml-2">
                  ${window.cartManager.formatPrice(originalTotal)}
                </span>
              ` : ''}
            </div>
            ${(() => {
              // Handle properties - Shopify cart.js API returns properties as an object {key: value}
              if (!item.properties) return '';
              // Properties can be an object (from cart.js) or array (from cart/add.js response)
              let preferredLetter = null;
              if (Array.isArray(item.properties)) {
                // Array format: [{name: "Preferred Letter", value: "A"}]
                const prop = item.properties.find(p => p.name === 'Preferred Letter');
                preferredLetter = prop ? prop.value : null;
              } else if (typeof item.properties === 'object') {
                // Object format: {"Preferred Letter": "A"}
                preferredLetter = item.properties['Preferred Letter'] || null;
              }
              if (preferredLetter) {
                return `<p class="text-xs text-[#404040] mt-1">Preferred Letter: <span class="font-medium">${preferredLetter}</span></p>`;
              }
              return '';
            })()}
          </div>

          <!-- Quantity and Remove Controls -->
          <div class="flex items-center justify-between">
            <div class="flex flex-col gap-1">
              <div class="flex items-center border border-gray-300 rounded-full w-fit shrink-0 overflow-hidden">
                <button
                  type="button"
                  aria-label="Decrease quantity"
                  class="quantity-decrease w-11 h-11 shrink-0 flex items-center justify-center leading-none hover:bg-gray-100 text-gray-600 hover:text-gray-800"
                  data-line-key="${item.key}"
                  data-quantity="${item.quantity - 1}"
                >
                  -
                </button>
                <input
                  type="number"
                  value="${item.quantity}"
                  min="1"
                  ${tLimit > 0 ? `max="${tLimit}"` : ''}
                  aria-label="Quantity"
                  class="quantity-input w-10 h-11 shrink-0 text-center text-[14px] leading-none border-0 p-0 focus:ring-0 bg-transparent"
                  data-line-key="${item.key}"
                >
                <button
                  type="button"
                  aria-label="Increase quantity"
                  class="quantity-increase w-11 h-11 shrink-0 flex items-center justify-center leading-none hover:bg-gray-100 text-gray-600 hover:text-gray-800${tAtLimit ? ' opacity-40 cursor-not-allowed' : ''}"
                  data-line-key="${item.key}"
                  data-quantity="${item.quantity + 1}"
                  ${tLimit > 0 ? `data-max-quantity="${tLimit}"` : ''}
                  ${tAtLimit ? `disabled aria-disabled="true" title="Limit ${tLimit} per order"` : ''}
                >
                  +
                </button>
              </div>
              ${tAtLimit ? `<p class="tierly-limit-note text-xs text-[#E13F39] leading-tight">Limit ${tLimit} per order</p>` : ''}
            </div>

            <!-- Remove Button -->
            <button
              type="button"
              class="remove-item min-w-11 min-h-11 flex items-center justify-center text-gray-500 hover:text-red-600 p-1 rounded hover:bg-red-50"
              data-line-key="${item.key}"
              aria-label="Remove item"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
      ${addonRowsHTML}
      </div>
    `;
  }

  // Render an add-on as a compact nested row inside its parent's card
  renderAddonRow(item) {
    const imageUrl = item.image ? window.cartManager.formatImageUrl(item.image, 80, 80) : '';
    const itemTotal = item.final_line_price || (item.price * item.quantity);

    return `
      <div class="cart-drawer-addon flex items-center gap-3 px-4 py-3 bg-gray-50" data-variant-id="${item.variant_id}">
        <!-- Small image -->
        <div class="w-12 h-12 flex-shrink-0 rounded-[4px] overflow-hidden bg-gray-200">
          ${imageUrl ? `
            <img
              src="${imageUrl}"
              alt="${this.escapeHtml(item.product_title)}"
              width="48"
              height="48"
              class="w-full h-full object-cover"
            >
          ` : ''}
        </div>

        <!-- Label, title + price -->
        <div class="flex-1 min-w-0">
          <p class="text-[10px] font-semibold uppercase text-gray-500 tracking-wide leading-none">Add-on</p>
          <p class="text-[12px] font-medium text-black truncate mt-[2px]">${this.escapeHtml(item.product_title)}</p>
          <p class="text-[12px] text-[#B72621] font-semibold">+${window.cartManager.formatPrice(itemTotal)}</p>
        </div>

        <!-- Remove only (no qty controls for add-ons) -->
        <button
          type="button"
          class="remove-item min-w-11 min-h-11 flex items-center justify-center text-gray-500 hover:text-red-600 p-1 rounded hover:bg-red-50 flex-shrink-0"
          data-line-key="${item.key}"
          aria-label="Remove add-on"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
          </svg>
        </button>
      </div>
    `;
  }

  updateCartFooter(cartData) {
    const subtotalElement = document.getElementById('cart-drawer-subtotal');
    if (subtotalElement) {
      // Compute visible subtotal (exclude hidden accessory products)
      const hidden = window.HIDDEN_CART_HANDLES || [];
      const visibleSubtotal = (cartData.items || []).reduce((sum, item) => {
        return hidden.includes(item.handle) ? sum : sum + (item.final_line_price || item.price * item.quantity);
      }, 0);
      subtotalElement.textContent = window.cartManager.formatPrice(visibleSubtotal);
    }
  }

  updateFreeShippingTracker(cartData) {
    const tracker = document.querySelector('[data-threshold]');
    if (!tracker) return;

    // Get threshold from data attribute or use default
    const threshold = parseInt(tracker.dataset.threshold) || 50000;
    // Use visible total (exclude hidden accessory products)
    const hidden = window.HIDDEN_CART_HANDLES || [];
    const cartTotal = (cartData.items || []).reduce((sum, item) => {
      return hidden.includes(item.handle) ? sum : sum + (item.final_line_price || item.price * item.quantity);
    }, 0);
    const amountNeeded = Math.max(0, threshold - cartTotal);
    const progressPercentage = Math.min(100, (cartTotal / threshold) * 100);

    // Update subtotal
    const subtotalValue = tracker.querySelector('[data-subtotal-value]');
    if (subtotalValue) {
      subtotalValue.textContent = window.cartManager.formatPrice(cartTotal);
    }

    // Update message
    const message = tracker.querySelector('[data-shipping-message]');
    if (message) {
      if (amountNeeded > 0) {
        message.innerHTML = `You're only <span class="text-[#E13F39]">${window.cartManager.formatPrice(amountNeeded)}</span> away from Free Shipping!`;
      } else {
        message.textContent = "You've qualified for Free Shipping!";
      }
    }

    // Update progress bar
    const progressFill = tracker.querySelector('.free-shipping-tracker__progress-bar-fill');
    if (progressFill) {
      progressFill.style.width = `${progressPercentage}%`;
    }
  }

}

// Initialize cart drawer
let cartDrawer;
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    cartDrawer = new CartDrawer();
  });
} else {
  cartDrawer = new CartDrawer();
}

// Make it globally accessible
window.cartDrawer = cartDrawer; 